import React from 'react';
import {Table, Select, Button, Modal, message, Space, Card} from 'antd';
import {CopyOutlined, DeleteOutlined, LinkOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import {applicationConfig} from '../../../Util/config';
import HeaderSearchCard from '../../CommonComponents/HeaderSearchCard';
import {
  PageSizeArray,
  PaginationPosition,
  displayFileName,
} from '../../../Util/commonUtility';
import {CREATE} from '../../../Constant/enums';
import useLocalization from '../../../Localization';
import './PbssFiles.scss';

const FILE_TYPE_OPTIONS = [
  {label: 'PBSS International', value: 'pbss_international'},
  {label: 'General', value: 'general'},
  {label: 'All', value: 'all'},
];

const FORMAT_OPTIONS = [
  {label: 'All formats', value: 'all'},
  {label: 'PDF', value: 'pdf'},
  {label: 'Images (JPG, PNG)', value: 'image'},
];

const PbssFilesComponent = ({
  files = [],
  loading,
  paging,
  onPaginationChange,
  onFilterChange,
  onFormatFilterChange,
  selectedType,
  selectedFormat,
  onDelete,
  fetchList,
}) => {
  const navigate = useNavigate();
  const {ApplicationString} = useLocalization();

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url).then(
      () => message.success('File URL copied to clipboard'),
      () => message.error('Failed to copy'),
    );
  };

  const handleDeleteClick = (record) => {
    Modal.confirm({
      title: 'Delete file',
      content: `Are you sure you want to delete "${displayFileName(record)}"?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        const ok = await onDelete(record.id);
        if (ok) fetchList();
      },
    });
  };

  const getFileUrl = (name) => {
    return `${applicationConfig.UPLOAD_URL}/${name}`;
  };

  const columns = [
    {
      title: 'File Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (_, record) => {
        const label = displayFileName(record);
        return <span title={label}>{label}</span>;
      },
    },
    {
      title: 'File URL',
      dataIndex: 'name',
      key: 'url',
      ellipsis: true,
      render: (name) => {
        const url = getFileUrl(name);
        return (
          <a href={url} target='_blank' rel='noopener noreferrer'>
            <LinkOutlined /> {url}
          </a>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space>
          <Button
            type='link'
            size='small'
            icon={<CopyOutlined />}
            onClick={() => handleCopyUrl(getFileUrl(record.name))}>
            Copy URL
          </Button>
          <Button
            type='link'
            danger
            size='small'
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteClick(record)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const headerExtra = (
    <div className='pbss-files-header-filters'>
      <Select
        placeholder='Filter by type'
        value={selectedType || 'pbss_international'}
        onChange={onFilterChange}
        options={FILE_TYPE_OPTIONS}
        className='pbss-files-select-type'
      />
      <Select
        placeholder='File format'
        value={selectedFormat || 'all'}
        onChange={onFormatFilterChange}
        options={FORMAT_OPTIONS}
        className='pbss-files-select-format'
      />
    </div>
  );

  return (
    <div className='pbss-files-page main-container'>
      <HeaderSearchCard
        showFilter={false}
        showAddButton={true}
        resource='pbss_files'
        permission={CREATE}
        buttonTitle={
          ApplicationString['pbssFilesComponent.button.addPbssFiles'] || 'Add'
        }
        addClick={() => navigate('/admin/pbssFiles/add')}
        dataTestId='adminPbssFiles-button-add'
        extra={headerExtra}
      />
      <Card className='pbss-files-table-card' bordered={false}>
        <Table
          bordered
          className='admin-table pbss-files-table'
          columns={columns}
          dataSource={files}
          rowKey='id'
          loading={loading}
          pagination={{
            current: paging?.page || 1,
            total: paging?.totalCount || 0,
            pageSize: paging?.pageCount || 10,
            pageSizeOptions: PageSizeArray,
            showSizeChanger: true,
            showQuickJumper: true,
            position: [PaginationPosition],
            showTotal: (total) => `Total ${total} items`,
            onChange: (page, pageSize) => onPaginationChange(page, pageSize),
          }}
        />
      </Card>
    </div>
  );
};

export default PbssFilesComponent;
